import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getWatches, updateCardWatches, addNonPsaWatch, removeNonPsaWatch } from '@/api/watches';
import { getCardStats } from '@/api/card';
import CardItemView from '@/components/CardItemView';
import FavoriteDialog from '@/components/FavoriteDialog';
import dondonGreyLogo from '@/assets/dondon_grey.svg';
import heartFilledIcon from '@/assets/heart_filled.svg';
import { useToast } from '@/components/ToastProvider';
import { formatCurrency } from '@/utils/formatUtils';
import { getWatchTypeDisplay } from '@/utils/watchUtils';
import Button from '@/components/Button';

const FAVORITES_STATE_KEY = 'favorites_page_state';

const getWatchKey = (watch) => {
  const listingId = watch.listingId || '';
  return `${watch.productId}-${watch.type}-${listingId}`;
};

const FavoritesPage = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [hoveredCardId, setHoveredCardId] = useState(null);
  const [selectedCardId, setSelectedCardId] = useState(null);
  const [selectedCardType, setSelectedCardType] = useState(null);
  const [isFavoriteDialogOpen, setIsFavoriteDialogOpen] = useState(false);
  const [favoriteSelectionsData, setFavoriteSelectionsData] = useState([]);
  const [currentFavoritedItems, setCurrentFavoritedItems] = useState([]);
  const [cursor, setCursor] = useState(null);
  const [accumulatedWatches, setAccumulatedWatches] = useState([]);
  const [cachedNextCursor, setCachedNextCursor] = useState(null);
  const [hasMoreFromCache, setHasMoreFromCache] = useState(false);
  const lastLoadedCursorRef = useRef(null);
  const [restoredFromCache, setRestoredFromCache] = useState(false);
  const restoredEntriesRef = useRef(false);
  
  const { data: watchesData, isLoading, isFetching } = useQuery({
    queryKey: ['watches', cursor],
    queryFn: () => getWatches(cursor)
  });

  // Restore cached state (accumulated data + next cursor) on mount
  useEffect(() => {
    const cachedState = sessionStorage.getItem(FAVORITES_STATE_KEY);
    if (cachedState) {
      try {
        const parsed = JSON.parse(cachedState);
        if (Array.isArray(parsed.accumulated)) {
          setAccumulatedWatches(parsed.accumulated);
          restoredEntriesRef.current = parsed.accumulated.length > 0;
        }
        if (typeof parsed.nextCursor === 'string' || parsed.nextCursor === null) {
          setCachedNextCursor(parsed.nextCursor);
        }
        if (typeof parsed.hasMore === 'boolean') {
          setHasMoreFromCache(parsed.hasMore);
        }
        if (typeof parsed.lastCursor === 'string' || parsed.lastCursor === null) {
          lastLoadedCursorRef.current = parsed.lastCursor ?? null;
        }
      } catch (error) {
        console.warn('Failed to parse favorites cache', error);
        sessionStorage.removeItem(FAVORITES_STATE_KEY);
      }
    }
    setRestoredFromCache(true);
  }, []);

  // Clear cached state on full page reload/close
  useEffect(() => {
    const handleBeforeUnload = () => {
      sessionStorage.removeItem(FAVORITES_STATE_KEY);
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);

  // Update accumulated watches when new data arrives
  useEffect(() => {
    if (!watchesData?.data) {
      return;
    }

    const incoming = watchesData.data;
    const incomingKeys = new Set(incoming.map(getWatchKey));
    const shouldPreserveCachedMeta = restoredEntriesRef.current && cursor === null;

    setAccumulatedWatches((prev) => {
      // cursor === null means we are handling the first page
      if (!cursor) {
        if (!restoredEntriesRef.current) {
          // Not restored from cache (for example, favorites_page_state was cleared on another page):
          // this is a fresh entry into FavoritesPage, so rely only on server data
          // and avoid keeping cancelled items from old cached pages.
          return [...incoming];
        }

        // Restored from cache and cursor === null (e.g. back navigation from a second page detail):
        // keep the original behavior of "first page on top, cached subsequent pages below".
        const filteredPrev = prev.filter((item) => !incomingKeys.has(getWatchKey(item)));
        return [...incoming, ...filteredPrev];
      }

      // cursor is not null: this is a subsequent page, de-duplicate then append
      const filteredPrev = prev.filter((item) => !incomingKeys.has(getWatchKey(item)));
      return [...filteredPrev, ...incoming];
    });

    if (!shouldPreserveCachedMeta) {
      setCachedNextCursor(watchesData.cursor ?? null);
      setHasMoreFromCache(Boolean(watchesData.cursor));
      lastLoadedCursorRef.current = cursor ?? null;
      restoredEntriesRef.current = false;
    }
  }, [watchesData, cursor]);

  // Persist accumulated data + pagination info to sessionStorage
  useEffect(() => {
    if (!restoredFromCache) return;
    const stateToCache = {
      accumulated: accumulatedWatches,
      nextCursor: cachedNextCursor ?? null,
      hasMore: hasMoreFromCache,
      lastCursor: lastLoadedCursorRef.current ?? null
    };
    sessionStorage.setItem(FAVORITES_STATE_KEY, JSON.stringify(stateToCache));
  }, [accumulatedWatches, cachedNextCursor, hasMoreFromCache, restoredFromCache]);

  const nextCursorToUse = restoredEntriesRef.current
    ? lastLoadedCursorRef.current
    : (watchesData?.cursor ?? cachedNextCursor);

  const hasMoreWatches = restoredEntriesRef.current
    ? hasMoreFromCache
    : Boolean(nextCursorToUse);
  const hasWatches = accumulatedWatches.length > 0;

  // Handle load more
  const handleLoadMore = () => {
    if (nextCursorToUse) {
      setCursor(nextCursorToUse);
      setCachedNextCursor(null);
      setHasMoreFromCache(false);
    }
  };

  // Add or update watch mutation for PSA cards and boxes
  const updateWatchMutation = useMutation({
    mutationFn: ({ id, type, options }) => {
      return updateCardWatches(id, type, options);
    },
    onSuccess: (_data, variables) => {
      // First invalidate server data to ensure subsequent pages are consistent
      queryClient.invalidateQueries(['watches']);

      const { id, type, options } = variables || {};

      // Keep local accumulatedWatches in sync to avoid cancelled/updated favorites lingering in cache
      if (!id || !type) return;

      setAccumulatedWatches((prev) => {
        return prev.reduce((acc, watch) => {
          if (watch.productId !== id || watch.type !== type) {
            acc.push(watch);
            return acc;
          }

          // PSA 单卡：用新的 psaGrades 覆盖；如果 options 为空则直接移除这条收藏
          if (type === 'PSA_SINGLE_CARD') {
            // PSA single card: overwrite psaGrades with new options; if options is empty, remove this watch
            if (!options || options.length === 0) {
              return acc;
            }
            acc.push({
              ...watch,
              psaGrades: options,
            });
            return acc;
          }

          if (type === 'CARD_BOX') {
            // Box card: overwrite bundledQuantities; if options is empty, remove this watch
            if (!options || options.length === 0) {
              return acc;
            }
            acc.push({
              ...watch,
              bundledQuantities: options,
            });
            return acc;
          }

          // Any unexpected type: keep the original watch as-is
          acc.push(watch);
          return acc;
        }, []);
      });
    },
    onError: (error) => {
      toast(`Error: ${error.message}`);
    }
  });
  
  // Remove watch mutation for non-PSA cards
  const removeWatchMutation = useMutation({
    mutationFn: ({ productId, listingId }) => removeNonPsaWatch(productId, listingId),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries(['watches']);

      const { productId, listingId } = variables || {};
      if (!productId || !listingId) return;

      // 从本地 accumulatedWatches 中移除对应的 NON_PSA_SINGLE_CARD 记录，
      // 避免它在后续合并时被追加到列表最后。
      setAccumulatedWatches((prev) =>
        prev.filter(
          (watch) =>
            !(
              watch.type === 'NON_PSA_SINGLE_CARD' &&
              watch.productId === productId &&
              watch.listingId === listingId
            )
        )
      );
    },
    onError: (error) => {
      toast(`Error: ${error.message}`);
    }
  });

  // Handle opening the dialog for PSA cards or boxes
  const handleCardFavoriteClick = async (productId, type, currentFavorites, boxType) => {
    if (type === 'NON_PSA_SINGLE_CARD') {
      // For non-PSA cards, simply remove them (since they're already favorited in the favorites page)
      const listingId = accumulatedWatches.find(watch => 
        watch.productId === productId && watch.type === 'NON_PSA_SINGLE_CARD'
      )?.listingId;
      
      if (listingId) {
        removeWatchMutation.mutate({ productId, listingId });
      }
      return;
    }
    
    // For PSA cards and boxes, we need to fetch card stats and show the dialog
    setSelectedCardId(productId);
    setSelectedCardType(type);
    
    try {
      // Get card stats to show prices in the dialog
      const response = await queryClient.fetchQuery({
        queryKey: ['cardStats', productId],
        queryFn: () => getCardStats(productId)
      });
      
      let selections = [];
      
      if (type === 'PSA_SINGLE_CARD') {
        // Transform PSA stats for the favorite dialog
        selections = response.data.psaStats.map(stat => ({
          id: stat.psa.toLowerCase().replace('_', ''),
          label: stat.psa.replace('PSA_', 'PSA '),
          subLabel: stat.lowestPriceOnSale ? formatCurrency(stat.lowestPriceOnSale) : t('common.sold_out', 'Sold Out'),
        }));
        
        // Set currently favorited grades
        setCurrentFavoritedItems(currentFavorites.map(grade => grade.toLowerCase().replace('_', '')));
      } else if (type === 'CARD_BOX') {
        // Transform box quantities for the favorite dialog
        const unit = boxType === 'PACK' ? t('box.pack') : t('box.box_unit');
        selections = response.data.bundledQuantityStats.map(stat => ({
          id: stat.quantity.toString(),
          label: `${stat.quantity} ${unit}`,
          subLabel: stat.lowestPriceOnSale ? formatCurrency(stat.lowestPriceOnSale) : t('common.sold_out', 'Sold Out'),
        }));
        
        // Set currently favorited quantities
        setCurrentFavoritedItems(currentFavorites.map(qty => qty.toString()));
      }
      
      setFavoriteSelectionsData(selections);
      setIsFavoriteDialogOpen(true);
    } catch (error) {
      toast(`Error: ${error.message}`);
    }
  };

  // Handle dialog confirmation
  const handleFavoriteConfirm = (selectedIds) => {
    if (!selectedCardId || !selectedCardType) return;
    
    // For single cards, convert the lowercase IDs back to PSA format
    const options = selectedCardType === 'PSA_SINGLE_CARD'
      ? selectedIds.map(id => id.toUpperCase().replace('PSA', 'PSA_'))
      : selectedIds.map(id => parseInt(id));
    
    updateWatchMutation.mutate({
      id: selectedCardId,
      type: selectedCardType,
      options
    });
    
    // Reset state
    setSelectedCardId(null);
    setSelectedCardType(null);
  };

  // Handle removing all favorites
  const handleRemoveAllFavorites = () => {
    if (!selectedCardId || !selectedCardType) return;
    
    updateWatchMutation.mutate({
      id: selectedCardId,
      type: selectedCardType,
      options: []
    });
    
    // Reset state
    setSelectedCardId(null);
    setSelectedCardType(null);
  };

  return (
    <div className="mx-auto bg-white rounded-lg shadow-sm p-6">
      {/* Header */}
      <h1 className="text-lg font-bold mb-6">{t('me.menu.favorites')}</h1>
      
      {isLoading && !cursor ? (
        <div className="h-[800px] flex justify-center items-center">
          <span className="text-gray-400">{t('common.loading')}</span>
        </div>
      ) : !hasWatches ? (
        <div className="h-[800px] flex flex-col justify-center items-center gap-6">
          <img src={dondonGreyLogo} alt="DonDon" className="w-24 h-16 opacity-50" />
          <div className="text-center text-[#aaaaaa] text-sm font-normal leading-tight">
            {t('favorite.empty')}
          </div>
        </div>
      ) : (
        <div className="min-h-[800px] overflow-y-auto">
          <div className="grid grid-cols-4 max:grid-cols-5 gap-4">
            {accumulatedWatches.map((watch) => {
              const { watchable, type, productId } = watch;
              
              // Skip if watchable is not available
              if (!watchable) return null;
                
              // Get listing ID for NON_PSA_SINGLE_CARD type
              const listingId = type === 'NON_PSA_SINGLE_CARD' ? watch.listingId : null;
              
              // Get watch type display
              const watchTypeText = getWatchTypeDisplay(watch, t);
              
              // For PSA cards, we need the selected grades
              const psaGrades = type === 'PSA_SINGLE_CARD' ? watch.psaGrades : [];
              
              // For box cards, we need the selected quantities
              const bundledQuantities = type === 'CARD_BOX' ? watch.bundledQuantities : [];
              
              // Generate a unique key for the card
              const cardKey = getWatchKey(watch);

              return (
                <div 
                  key={cardKey} 
                  className="relative"
                  onMouseEnter={() => setHoveredCardId(cardKey)}
                  onMouseLeave={() => setHoveredCardId(null)}
                >
                  <CardItemView
                    id={listingId || productId}
                    type={type}
                    image={watchable.image?.url}
                    brand={watchable.brand?.localizedName}
                    name={watchable.localizedRawName || watchable.localizedName}
                    price={watchable.currentPrice || 0}
                    code={watchable.code}
                    rawCode={watchable.rawCode}
                    set={watchable.set}
                    rarityInfo={watchable.rarityInfo}
                    isListing={!!listingId}
                    customRightText={watchTypeText}
                    className="w-full"
                  />
                  
                  {/* Favorite button overlay - only show on hover */}
                  {hoveredCardId === cardKey && (
                    <button 
                      onClick={() => handleCardFavoriteClick(
                        productId, 
                        type, 
                        type === 'PSA_SINGLE_CARD' ? psaGrades : bundledQuantities,
                        watchable.boxType
                      )}
                      className="absolute top-2 right-2 w-8 h-8 p-2.5 bg-black/60 rounded inline-flex justify-center items-center gap-2.5"
                    >
                      <img src={heartFilledIcon} alt="Favorite" className="w-4 h-4" />
                    </button>
                  )}
                </div>
              );
            })}
          </div>
          
          {/* Loading indicator for pagination */}
          {isFetching && cursor && (
            <div className="w-full flex justify-center items-center py-4 mt-4">
              <p>{t('common.loading')}</p>
            </div>
          )}
          
          {/* Load More Button */}
          {hasMoreWatches && (
            <div className="flex justify-center mt-8">
              <Button 
                variant="default" 
                size="m" 
                onClick={handleLoadMore}
                disabled={isFetching}
              >
                {isFetching && cursor ? t('common.loading') : t('home.actions.load_more')}
              </Button>
            </div>
          )}
        </div>
      )}
      
      {/* Favorite Dialog */}
      {isFavoriteDialogOpen && (
        <FavoriteDialog
          onOpenChange={setIsFavoriteDialogOpen}
          selections={favoriteSelectionsData}
          onConfirm={handleFavoriteConfirm}
          onRemoveAll={handleRemoveAllFavorites}
          initialSelectedIds={currentFavoritedItems}
          title={selectedCardType === 'PSA_SINGLE_CARD' ? t('favorite.title') : t('favorite.title_box')}
        />
      )}
    </div>
  );
};

export default FavoritesPage; 